import { SalesReportError } from '@/components/money/SalesReportError'
import { LoadingState } from '@/components/patterns/loading-state'
import { canDisplaySalesReport } from '@/lib/services/money/money.query'
import { usePathHelper } from '@/hooks/usePathHelper'
import { adminTitleHead } from '@/lib/admin-route-title'
import {
  organizationQueries,
  useOrganization,
} from '@/lib/services/organizations/organizations.query'
import {
  getSalesSummaryReportFiltersFromSearch,
  parseSalesSummaryGranularity,
  resolveSalesSummaryGranularityForRange,
  salesSummaryReportSearchSchema,
  type SalesSummaryReportFilters,
} from '@/lib/services/reports/reports.schema'
import {
  reportQueries,
  useExportSalesSummaryReportMutation,
  useSalesSummaryReport,
} from '@/lib/services/reports/reports.query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { BarChart3 } from 'lucide-react'
import { toast } from 'sonner'
import { startTransition } from 'react'
import { getDefaultSalesReportFilters, SalesReportToolbar } from '../-sales-report-toolbar'
import { downloadExportedReport, getExportErrorMessage } from '../-helpers'
import { hasSalesSummaryActivity } from './-helpers'
import { SalesSummaryReportResults } from './-results'

export const Route = createFileRoute('/_authenticated/$orgId/reports/sales-statistics/')({
  component: SalesSummaryReportPage,
  validateSearch: (search) => salesSummaryReportSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context, deps: { search }, params }) => {
    const organization =
      context.queryClient.getQueryData?.(organizationQueries.show(params.orgId).queryKey) ??
      (await context.queryClient.ensureQueryData(organizationQueries.show(params.orgId)))
    const filters = getReportFilters(search, organization?.timezone)

    await context.queryClient.prefetchQuery(
      reportQueries.salesSummary({
        organizationId: params.orgId,
        filters,
      })
    )

    return { organization }
  },
  // After the loader, so its data's type is inferred first.
  head: ({ loaderData }) =>
    adminTitleHead(
      loaderData?.organization?.type === 'global'
        ? 'Global Payments Collected'
        : 'Sales Statistics',
      'Reports'
    ),
})

export function SalesSummaryReportPage() {
  const path = usePathHelper()
  const { orgId } = Route.useParams()
  const search = Route.useSearch()
  const { data: organization } = useOrganization(orgId)
  const filters = getReportFilters(search, organization?.timezone)
  const {
    data: report,
    error,
    isError,
    isPending,
    isFetching,
    refetch,
  } = useSalesSummaryReport(filters, { retryOnMount: false })
  const showReport = canDisplaySalesReport({ data: report, error, isError })
  const exportMutation = useExportSalesSummaryReportMutation()
  const hasExportData = showReport && hasSalesSummaryActivity(report)
  const routeLink = path.salesStatistics.index().using({ orgId }).link
  const navigate = useNavigate()
  const isGlobalReport = Boolean(report?.is_global || organization?.type === 'global')
  const reportTitle = isGlobalReport ? 'Global Payments Collected' : 'Sales Statistics'
  const handleGranularityChange = (granularity: string) => {
    const resolvedGranularity = resolveSalesSummaryGranularityForRange(
      parseSalesSummaryGranularity(granularity),
      filters
    )

    startTransition(() => {
      void navigate({
        ...routeLink,
        search: {
          start_date: filters.start_date,
          end_date: filters.end_date,
          date_mode: filters.date_mode ?? undefined,
          source: filters.source ?? undefined,
          fulfillment_type: filters.fulfillment_type ?? undefined,
          granularity: resolvedGranularity,
          search: filters.search ?? undefined,
          sort: filters.sort ?? undefined,
          page_size: filters.page_size ?? undefined,
          page: 1,
        },
        replace: Boolean(
          search.start_date ||
          search.end_date ||
          search.date_mode ||
          search.source ||
          search.fulfillment_type ||
          search.granularity ||
          search.search ||
          search.sort ||
          search.page ||
          search.page_size
        ),
      })
    })
  }

  const handleExport = async () => {
    try {
      const response = await exportMutation.mutateAsync({
        organizationId: orgId,
        filters,
      })

      downloadExportedReport(
        response,
        `${isGlobalReport ? 'global-sales-order-report' : 'sales-statistics'}-${filters.start_date}-to-${filters.end_date}.xlsx`
      )
    } catch (error) {
      toast.error(
        await getExportErrorMessage(error, `Failed to export ${reportTitle.toLowerCase()}`)
      )
    }
  }

  return (
    <div className="space-y-4 pb-6">
      <SalesReportToolbar
        title={reportTitle}
        description={isGlobalReport ? 'Counted on the day payment was collected' : undefined}
        icon={BarChart3}
        routeLink={routeLink}
        search={search}
        filters={filters}
        organization={organization}
        canExport
        exportDisabled={!hasExportData}
        exportPending={exportMutation.isPending}
        onExport={handleExport}
      />

      {isError && (
        <SalesReportError
          error={error}
          orgId={orgId}
          onRetry={() => refetch()}
          isRetrying={isFetching}
          hasCachedReport={showReport}
        />
      )}
      {isPending ? (
        <LoadingState label="Loading sales statistics…" />
      ) : showReport && report ? (
        <SalesSummaryReportResults
          report={report}
          orgId={orgId}
          routeLink={routeLink}
          search={search}
          onGranularityChange={handleGranularityChange}
        />
      ) : null}
    </div>
  )
}

function getReportFilters(
  search: unknown,
  organizationTimezone: string | undefined
): SalesSummaryReportFilters {
  const defaultFilters = getDefaultSalesReportFilters(organizationTimezone)

  return getSalesSummaryReportFiltersFromSearch(search, defaultFilters)
}
